import { coreError } from "./core-error";
import { moneefloApi } from "./client";

/*
 * The generic orders page: orders for products with no orders tab of their
 * own (visa, travel, study abroad, loan). Xano kept these in a table of
 * their own; here they are ordinary orders, and the page asks for the
 * categories that have nowhere else to be listed. Forex and remittance
 * orders are left to their own tabs, so an order is listed in one place.
 */

/** Product categories whose orders are only seen on the generic page. */
export const GENERIC_CATEGORIES = [
  "visa",
  "travel",
  "study_abroad",
  "loan",
  "other",
];

const CATEGORY_LABEL: Record<string, string> = {
  visa: "Visa",
  travel: "Travel",
  study_abroad: "Study Abroad",
  loan: "Loan",
  other: "Others",
};

/** The words the page colours and gates its buttons by. */
const GENERIC_STATUS: Record<string, string> = {
  draft: "Order Created",
  quoted: "Order Created",
  confirmed: "Order Accepted",
  processing: "processing",
  on_hold: "On Hold",
  fulfilled: "Order Successful",
  cancelled: "Order Cancelled",
  refunded: "Order Closed",
};

/** The house prefix is left off; the page puts its own ORD- in front. */
const bareNo = (orderNo: string) => orderNo.replace(/^MF-/, "");

const rupees = (minor: string | null | undefined) =>
  Number(minor ?? 0) / 100;

type OrderListRow = {
  id: string;
  orderNo: string;
  status: string;
  product: string | null;
  customer: { id: string; name: string };
  totalPayableMinor: string | null;
  placedAt: string | null;
};

/** The products the page's filter offers, grouped by what they are. */
export async function getGenericProducts(): Promise<{
  data: { id: string; name: string; subCategory: string }[];
}> {
  const { data } = await moneefloApi.get("/catalog/products");
  const products = (data ?? []) as {
    id: string;
    name: string;
    category: string;
  }[];
  return {
    data: products
      .filter((p) => GENERIC_CATEGORIES.includes(p.category))
      .map((p) => ({
        id: p.id,
        name: p.name,
        subCategory: CATEGORY_LABEL[p.category] ?? p.category,
      })),
  };
}

/**
 * Every generic order matching the search and product filter.
 *
 * The page shows them in one unpaged table, as it did with Xano, so this
 * walks the pages. There are few of these; the cap stops a runaway loop.
 */
export async function getGenericOrderList(payload: {
  params?: { q?: string; product_id?: (string | number)[] };
}): Promise<{ data: { items: unknown[] } }> {
  const search = payload.params?.q?.trim() || undefined;
  const productIds = (payload.params?.product_id ?? []).map(String);
  const perPage = 100;
  const rows: OrderListRow[] = [];
  try {
    for (let page = 1; page <= 20; page++) {
      const { data } = await moneefloApi.get("/orders", {
        params: {
          category: GENERIC_CATEGORIES.join(","),
          productIds: productIds.length > 0 ? productIds.join(",") : undefined,
          search,
          page,
          perPage,
        },
      });
      const items = (data?.items ?? []) as OrderListRow[];
      rows.push(...items);
      if (items.length < perPage || rows.length >= Number(data?.total ?? 0)) {
        break;
      }
    }
  } catch (error) {
    throw coreError(error, "The orders could not be loaded.");
  }

  return {
    data: {
      items: rows.map((order) => ({
        id: bareNo(order.orderNo),
        created_at: order.placedAt,
        product: [
          { name: order.product ?? "-", customer: { full_name: order.customer.name } },
        ],
        total_payable: rupees(order.totalPayableMinor),
        maxx_status: GENERIC_STATUS[order.status] ?? order.status,
      })),
    },
  };
}

type OrderDetail = {
  id: string;
  orderNo: string;
  status: string;
  customer: { id: string; name: string; phone: string | null; email: string | null };
  product: string | null;
  totalPayableMinor: string | null;
  placedAt: string | null;
  createdBy: { name: string } | null;
  channel: { origin: string | null };
  invoiceNo: string | null;
  invoiceDate: string | null;
  lead: { id: string; leadNo: number } | null;
};

async function loadOrder(orderId: string): Promise<OrderDetail> {
  try {
    const { data } = await moneefloApi.get(
      `/orders/${encodeURIComponent(orderId)}`,
    );
    return data as OrderDetail;
  } catch (error) {
    throw coreError(error, "The order could not be loaded.");
  }
}

/**
 * One generic order, in the shape the page reads.
 *
 * An order converted from a lead carries one total and no breakdown, so it
 * goes across as a single line with no tax or charges, rather than splitting
 * it into figures nobody entered.
 */
export async function getGenericOrderDetails(orderId: string): Promise<any> {
  const order = await loadOrder(orderId);
  // The invoice PDF filed when the lead was converted, if there is one. A
  // link it cannot fetch is not worth failing the page over.
  const invoice = await moneefloApi
    .get(`/orders/${encodeURIComponent(order.id)}/documents`)
    .then(({ data }) =>
      ((data?.documents ?? []) as {
        docType: string;
        downloadable?: boolean;
        url?: string | null;
        uploadedAt?: string;
      }[])
        .filter((d) => d.docType === "invoice" && d.downloadable && d.url)
        .sort((a, b) => (b.uploadedAt ?? "").localeCompare(a.uploadedAt ?? ""))[0],
    )
    .catch(() => undefined);
  const total = rupees(order.totalPayableMinor);
  const by = order.createdBy?.name ?? "";
  return {
    id: bareNo(order.orderNo),
    created_at: order.placedAt,
    staff: { fname: by },
    owner: { fname: by },
    assigned: null,
    maxx_status: GENERIC_STATUS[order.status] ?? order.status,
    branch_name: { name: order.channel?.origin ?? "" },
    linked_lead_id: order.lead ? [order.lead.leadNo] : [],
    invoice_no: order.invoiceNo,
    invoice_date: order.invoiceDate,
    invoice_url: invoice?.url ?? null,
    product: [
      {
        id: order.id,
        name: order.product ?? "-",
        subCategory: "",
        quantity: 1,
        inr_amount: total,
        gst: 0,
        other_charge: 0,
        created_for: {
          full_name: order.customer.name,
          email: order.customer.email ?? "",
          phone_number: order.customer.phone ?? "",
        },
      },
    ],
    amount_before_taxes: total,
    amount_and_charges: { GST: { GST: 0 }, service_charge: { amount: 0 } },
    total_payable: total,
  };
}

/**
 * Close (completed) or cancel, the two buttons on the page.
 *
 * Closing marks the order fulfilled. The status machine only reaches that
 * through processing, so an accepted order is moved there first; each step
 * is still checked by the server.
 */
export async function updateGenericOrderStatus(data: {
  order_id: number | string;
  status: string;
}): Promise<void> {
  const order = await loadOrder(String(data.order_id));
  const move = (status: string) =>
    moneefloApi.post(`/orders/${encodeURIComponent(order.id)}/status`, {
      status,
    });
  try {
    if (data.status === "cancel") {
      await move("cancelled");
      return;
    }
    if (data.status === "close") {
      if (order.status === "confirmed" || order.status === "on_hold") {
        await move("processing");
      }
      await move("fulfilled");
      return;
    }
    throw new Error(`Unknown action: ${data.status}`);
  } catch (error) {
    throw coreError(error, "The order's status could not be changed.");
  }
}
